// ============================================================================
// /tasks — cross-facility task list. The AM's daily driver.
//
// Default view: Active (Open + In_Progress), sorted by priority desc then
// due_date asc. Filters: status, priority, facility, assignee, source.
// Sort: priority+due (default) | created date | facility.
// ============================================================================

import { isAmRole, isFinanceSeat, isHrSeat, isOpsSeat, isSalesSeat } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, X as XIcon } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  useToast,
} from '../../shared/components';
import type {
  TaskCategory,
  TaskPriority,
  TaskSource,
  TaskStatus,
  UserRole,
} from '../../shared/types';
import {
  useTasks,
  addComment,
  type TaskFilter,
  type TaskSortKey,
} from './hooks/use-task-data';
import { ResolveModal } from './task-action-modals';
import {
  useEmployeeOptions,
  useFacilityOptions,
  employeeLabel,
} from './hooks/use-task-form-options';
import { useAuth } from '../../shared/auth';
import TaskCard from './task-card';
import TaskForm from './task-form';

const STATUSES: TaskStatus[] = ['Open', 'In_Progress', 'Done', 'Deferred'];
const PRIORITIES: TaskPriority[] = ['High', 'Medium', 'Low'];
const SOURCES: TaskSource[] = [
  'Manual',
  'Feedback_Auto',
  'Inspection_Auto',
  'Supply_Auto',
];
const CATEGORIES: TaskCategory[] = [
  'Operations',
  'Finance',
  'HR',
  'Sub_Management',
  'Sales',
  'Other',
];

// Special assignee filter values that aren't an employee_id.
// 'me' resolves to the signed-in user's EID at filter time.
// 'me_or_unassigned' resolves to (me OR null).
// 'unassigned' is null only.
type AssigneeFilterValue = string | 'me' | 'me_or_unassigned' | 'unassigned';

interface RoleDefault {
  label: string;
  categories?: TaskCategory[];
  assignee?: AssigneeFilterValue;
}

// Default filter per role. Executive/superuser get no default so they see
// the whole org's task firehose; everyone else lands on their own queue.
function roleDefault(role: UserRole | null | undefined): RoleDefault | null {
  if (isFinanceSeat(role)) return { label: 'Finance · my queue', categories: ['Finance'], assignee: 'me_or_unassigned' };
  if (isHrSeat(role)) return { label: 'HR · my queue', categories: ['HR'], assignee: 'me_or_unassigned' };
  if (isSalesSeat(role)) return { label: 'Sales · my queue', categories: ['Sales'], assignee: 'me_or_unassigned' };
  if (isAmRole(role)) return { label: 'My facilities', assignee: 'me_or_unassigned' };
  if (isOpsSeat(role)) return { label: 'Operations · my queue', categories: ['Operations'], assignee: 'me_or_unassigned' };
  // Executive, superuser and a sign-in still waiting for a role: no default.
  return null;
}

export default function TaskList() {
  const navigate = useNavigate();
  const { employee, profile } = useAuth();
  const toast = useToast();
  const { facilities } = useFacilityOptions();
  const { employees } = useEmployeeOptions();
  const [searchParams] = useSearchParams();

  const myEid = employee?.employee_id ?? null;
  // Default filter snapshot — only computed once per role/eid, so user edits
  // don't get blown away by re-renders. Re-runs only if the signed-in user
  // or role changes (e.g. role grant in an admin panel).
  const defaults = useMemo(() => roleDefault(profile?.role), [profile?.role]);

  // URL params take precedence over role default so widget links land
  // exactly where the user expects.
  const urlCategory = searchParams.get('category') as TaskCategory | null;
  const urlAssignee = searchParams.get('assignee'); // 'me' | EID | null

  const [statuses, setStatuses] = useState<TaskStatus[]>(['Open', 'In_Progress']);
  const [priorities, setPriorities] = useState<TaskPriority[]>([]);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<AssigneeFilterValue | null>(() => {
    if (urlAssignee) return urlAssignee as AssigneeFilterValue;
    return defaults?.assignee ?? null;
  });
  const [categories, setCategories] = useState<TaskCategory[]>(() => {
    if (urlCategory) return [urlCategory];
    return defaults?.categories ?? [];
  });
  const [sources, setSources] = useState<TaskSource[]>([]);
  const [sort, setSort] = useState<TaskSortKey>('priority_due');
  const [formOpen, setFormOpen] = useState(false);
  const [resolveTaskId, setResolveTaskId] = useState<string | null>(null);
  // True after the user has touched any filter — prevents the role-default
  // chip from being applied after a manual clear.
  const [defaultDismissed, setDefaultDismissed] = useState(false);

  // If we land here with URL params after first render (deep link from a
  // widget while the page is already mounted), reflect that in state.
  useEffect(() => {
    if (urlCategory) setCategories([urlCategory]);
    if (urlAssignee) setAssignee(urlAssignee as AssigneeFilterValue);
  }, [urlCategory, urlAssignee]);

  // Resolve the assignee filter to concrete DB-friendly values.
  const resolved = useMemo<{
    assignedTo: string | null;
    includesUnassigned: boolean;
  }>(() => {
    if (assignee === null) return { assignedTo: null, includesUnassigned: false };
    if (assignee === 'me') return { assignedTo: myEid, includesUnassigned: false };
    if (assignee === 'me_or_unassigned')
      return { assignedTo: myEid, includesUnassigned: true };
    if (assignee === 'unassigned') return { assignedTo: null, includesUnassigned: true };
    return { assignedTo: assignee, includesUnassigned: false };
  }, [assignee, myEid]);

  const filter = useMemo<TaskFilter>(
    () => ({
      statuses: statuses.length > 0 ? statuses : undefined,
      priorities: priorities.length > 0 ? priorities : undefined,
      facilityId,
      assignedTo: resolved.assignedTo,
      assigneeIncludesUnassigned: resolved.includesUnassigned,
      sources: sources.length > 0 ? sources : undefined,
      categories: categories.length > 0 ? categories : undefined,
    }),
    [statuses, priorities, facilityId, resolved, sources, categories],
  );

  function clearDefault() {
    setCategories([]);
    setAssignee(null);
    setDefaultDismissed(true);
  }
  const showDefaultChip = Boolean(defaults && !defaultDismissed);

  const { data, isLoading, error, refetch, updateStatus } = useTasks(
    filter,
    sort,
  );

  const onComplete = useCallback(
    async (id: string) => {
      try {
        await updateStatus(id, 'Done', {
          completed_by: employee?.employee_id ?? null,
        });
      } catch {
        /* optimistic UI rolls back inside updateStatus */
      }
    },
    [updateStatus, employee],
  );

  const onDefer = useCallback(
    async (id: string) => {
      const reason = window.prompt('Reason for deferral?') ?? '';
      try {
        await updateStatus(id, 'Deferred', {
          completion_notes: reason || null,
        });
      } catch {
        /* noop */
      }
    },
    [updateStatus],
  );

  const onReopen = useCallback(
    async (id: string) => {
      try {
        await updateStatus(id, 'Open');
      } catch {
        /* noop */
      }
    },
    [updateStatus],
  );

  const onEdit = useCallback(
    (id: string) => {
      navigate(`/tasks/${id}`);
    },
    [navigate],
  );

  const onResolveSubmit = useCallback(
    async (status: 'Done' | 'Deferred', note: string) => {
      if (!resolveTaskId || !employee?.employee_id) return;
      await addComment(resolveTaskId, note, employee.employee_id);
      await updateStatus(resolveTaskId, status, {
        completed_by: employee.employee_id,
        completion_notes: note,
      });
      toast.success(`Task ${status === 'Done' ? 'resolved' : 'deferred'}`, {
        description: resolveTaskId,
      });
    },
    [resolveTaskId, employee, updateStatus, toast],
  );

  const resolveTask = useMemo(
    () => data.find((t) => t.task_id === resolveTaskId) ?? null,
    [data, resolveTaskId],
  );

  const isEmpty = !isLoading && !error && data.length === 0;

  return (
    <div className="mx-auto max-w-5xl p-4 lg:p-6">
      <PageHeader
        title="Tasks"
        meta="What you need to handle, across all your facilities."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={refetch} aria-label="Reload">
              <RefreshCw size={14} className="mr-1" />
              Reload
            </Button>
            <Button onClick={() => setFormOpen(true)}>
              <Plus size={14} className="mr-1" />
              New task
            </Button>
          </div>
        }
      />

      <div className="mt-4 rounded-md border border-tcs-border bg-tcs-raised p-3">
        {/* Role-default chip — visible until user explicitly clears it. */}
        {showDefaultChip && (
          <div className="mb-3 flex items-center gap-2 text-xs">
            <span className="rounded-full bg-tcs-bg-muted px-2 py-0.5 text-tcs-text-secondary">
              Filter: {defaults?.label}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
              onClick={clearDefault}
              aria-label="Clear default filter"
            >
              <XIcon size={11} /> Clear
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-tcs-text-secondary">
          <MultiSelect
            label="Status"
            options={STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
            value={statuses}
            onChange={(v) => setStatuses(v as TaskStatus[])}
          />
          <MultiSelect
            label="Priority"
            options={PRIORITIES.map((p) => ({ value: p, label: p }))}
            value={priorities}
            onChange={(v) => setPriorities(v as TaskPriority[])}
          />
          <MultiSelect
            label="Category"
            options={CATEGORIES.map((c) => ({ value: c, label: c.replace('_', ' ') }))}
            value={categories}
            onChange={(v) => {
              setCategories(v as TaskCategory[]);
              setDefaultDismissed(true);
            }}
          />
          <SingleSelect
            label="Facility"
            value={facilityId ?? ''}
            onChange={(v) => setFacilityId(v || null)}
            options={[
              { value: '', label: 'All facilities' },
              ...facilities.map((f) => ({
                value: f.facility_id,
                label: f.facility_name ?? f.facility_id,
              })),
            ]}
          />
          <SingleSelect
            label="Assigned to"
            value={assignee ?? ''}
            onChange={(v) => {
              setAssignee((v as AssigneeFilterValue) || null);
              setDefaultDismissed(true);
            }}
            options={[
              { value: '', label: 'Anyone' },
              ...(myEid ? [{ value: 'me', label: 'Me' }] : []),
              ...(myEid
                ? [{ value: 'me_or_unassigned', label: 'Me or unassigned' }]
                : []),
              { value: 'unassigned', label: 'Unassigned' },
              ...employees.map((e) => ({
                value: e.employee_id,
                label: employeeLabel(e),
              })),
            ]}
          />
          <MultiSelect
            label="Source"
            options={SOURCES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
            value={sources}
            onChange={(v) => setSources(v as TaskSource[])}
          />
          <div className="ml-auto flex items-center gap-2 text-xs text-tcs-text-muted">
            <span>Sort</span>
            <select
              className="rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-xs"
              value={sort}
              onChange={(e) => setSort(e.target.value as TaskSortKey)}
            >
              <option value="priority_due">Priority + due date</option>
              <option value="created">Newest first</option>
              <option value="facility">Facility</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {error && (
          <ErrorState
            title="Could not load tasks"
            body={error}
            onRetry={refetch}
          />
        )}
        {isLoading && !error && (
          <div className="space-y-2">
            <Skeleton block height={64} />
            <Skeleton block height={64} />
            <Skeleton block height={64} />
          </div>
        )}
        {isEmpty && (
          <EmptyState
            title="All caught up"
            body="Create a task from any facility's Tasks tab, or use the New task button above."
          />
        )}
        {!isLoading && !error && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((t) => (
              <TaskCard
                key={t.task_id}
                task={t}
                showFacility
                currentEid={myEid}
                onResolve={(id) => setResolveTaskId(id)}
                onComplete={onComplete}
                onDefer={onDefer}
                onReopen={onReopen}
                onEdit={onEdit}
              />
            ))}
          </ul>
        )}
      </div>

      <TaskForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          refetch();
        }}
      />

      <ResolveModal
        open={resolveTaskId !== null}
        taskId={resolveTaskId}
        taskDescription={resolveTask?.description}
        onClose={() => setResolveTaskId(null)}
        onSubmit={onResolveSubmit}
      />
    </div>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

function SingleSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
}) {
  return (
    <label className="flex items-center gap-1">
      <span className="text-xs uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <select
        className="rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function MultiSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  options: SelectOption[];
}) {
  const [open, setOpen] = useState(false);
  const summary = value.length === 0 ? 'Any' : `${value.length} selected`;
  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-xs uppercase tracking-wide text-tcs-text-muted">
          {label}:
        </span>
        <span className="text-tcs-text">{summary}</span>
      </button>
      {open && (
        <div
          className="absolute left-0 z-10 mt-1 w-48 rounded-md border border-tcs-border bg-tcs-overlay p-2 shadow-md"
          onMouseLeave={() => setOpen(false)}
        >
          {options.map((o) => {
            const checked = value.includes(o.value);
            return (
              <label
                key={o.value}
                className="flex items-center gap-2 px-1 py-1 text-xs text-tcs-text-secondary"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...value, o.value]);
                    else onChange(value.filter((v) => v !== o.value));
                  }}
                />
                {o.label}
              </label>
            );
          })}
          {value.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded border border-tcs-border px-1 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-surface"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
